import _ from 'lodash';
import request from 'superagent';
import moment from 'moment';
import * as types from '../actionTypes/irSearchActionTypes';
import * as docketActionTypes from '../actionTypes/docketsActionTypes';
import { requestCaseData } from './irSearchActions';
import { notify } from '../../components/common/Notification';

export function onFailure(responseError) {
  return dispatch => {
    console.error(responseError);

    const bodyError = _.get(responseError, ['response', 'body', 'error']) || [];
    const request_id = _.get(responseError, ['response', 'body', 'request_id']);
    const errorMessageExtra = request_id ? `When contacting support, please quote Request ID '${request_id}'` : '';

    // looks like status 400 is for validation errors and we show it 'as is' to the user
    if (bodyError.status === 400 && bodyError.detail) {
      return notify.error(bodyError.detail);
    }

    const errorMessages = {
      // We want to hide real messages from user since traces are confusing to them
      messages: ['Application is experiencing some issues. Please try again in a few minutes.', errorMessageExtra],
      // but we include the real message in the details so we receive it in the sentry report
      details: bodyError.detail ? [bodyError.detail] : [bodyError],
    };

    return dispatch({ type: types.SERVICE_ERROR, errors: errorMessages });
  };
}

export function getDocketsFromResp(response) {
  const dockets = response.body;

  let showMissingDockets = false;

  // if there are any missing dockets for the case, show the missing dockets panel
  if (dockets.docket_gaps && dockets.docket_gaps.length > 0) {
    showMissingDockets = true;
  }

  let content = [];

  // mark all items as visible when first pulled from service
  if (dockets.entries && dockets.entries.content) {
    content = dockets.entries.content.reduce((accum, next) => {
      accum.push({ ...next, isVisible: true });

      // check if the next entry is a missing docket
      const missing = dockets.docket_gaps.find(d => d.gap_end + 1 === next.docket_no);
      if (missing) {
        accum.push({ missing });
      }

      return accum;
    }, []);
  }

  delete dockets.entries.content;

  dockets.entries.data = content;
  dockets.entries.showMissingDockets = showMissingDockets;

  return dockets;
}

function hasMoreRecentFilingsDate(header) {
  const {
    latest_known_date_filed,
    latest_known_history_date_filed,
    latest_docket_number,
    latest_history_number,
  } = header;
  return (
    moment(latest_known_history_date_filed, 'MM/DD/YYYY') > moment(latest_known_date_filed, 'MM/DD/YYYY') ||
    parseInt(latest_docket_number, 10) < parseInt(latest_history_number, 10)
  );
}

export const getPartsPurchaseInfo = params => (dispatch, getState) => {
  function onSuccess(response) {
    if (!_.isEmpty(response.body.error)) {
      return dispatch(onFailure({ response }));
    }

    const { entries } = getState()
      .get('dockets')
      .toJS();

    const updatedEntriesData = entries.data.map(docket => {
      if (params.case_docket_sequence_numbers[0].sequence_numbers.includes(docket.docket_seq)) {
        // default to in-pacer
        docket.colorCode = 'in-pacer';

        const { docket_seq } = docket;
        const responseEntry = _.find(response.body, { sequence_number: docket_seq });

        if (responseEntry) {
          if (responseEntry.has_user_purchased_any_document) {
            docket.colorCode = 'purchased';
          } else {
            docket.colorCode = 'in-inforuptcy';
          }
        }
      }
      return docket;
    });

    dispatch({
      type: docketActionTypes.UPDATE_DOCKETS,
      entries: { ...entries, status: 'done', data: updatedEntriesData },
    });

    return Promise.resolve(response.body);
  }

  return request
    .post('/rpc/dockets/purchase_info')
    .set('Accept', 'application/json')
    .send(params)
    .then(onSuccess, responseError => dispatch(onFailure(responseError)));
};

export const getDockets = ({ receipt, ...params }) => (dispatch, getState) => {
  dispatch({
    type: types.RESET_SERVICE_ERROR,
  });

  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS,
    header: { status: 'loading' },
    entries: { status: 'loading' },
  });

  dispatch({
    type: docketActionTypes.SET_RECEIPT,
    receipt: receipt || {},
  });

  dispatch({
    type: docketActionTypes.UPDATE_SELECTED_DOCKET,
    selectedDocket: {
      status: 'done',
      docket_no: null,
      docket_seq: null,
      binder: {},
    },
  });

  const queryString = {
    sort_order: 'desc',
    sort_column: 'date_filed',
    include_gaps: true,
    include_binders: false,
    ...params,
  };

  // remove empty properties from params
  // service for whatever reason throws error if sending null param values
  const queryStringNullRemoved = _.omitBy(queryString, _.isNil);
  const { case_number, court_code } = params;

  return request
    .get(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets`)
    .query(queryStringNullRemoved)
    .then(
      response => {
        if (!_.isEmpty(response.body.error)) {
          dispatch({
            type: docketActionTypes.UPDATE_DOCKETS,
            header: { status: '' },
            entries: { status: '' },
          });

          return dispatch(onFailure({ response }));
        }

        const dockets = getDocketsFromResp(response);

        if (dockets.header) {
          dockets.header.hasMoreRecentFilingsDate = hasMoreRecentFilingsDate(dockets.header);
        }

        const { entries, header, links } = dockets;

        // using existing of header data to determine if it's actually done and ready to display
        const headerStatus = header.html ? 'done' : '';

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS,
          entries: { status: 'done', ...entries },
          header: { status: headerStatus, ...header },
          links,
        });

        // if contains entries, then refresh the purchseInfo so we can color the docket numbers
        if (entries.data && entries.data.length > 0) {
          const seqNumbers = _.get(entries, 'data').reduce((accum, next) => {
            if (next.docket_seq) {
              accum.push(next.docket_seq);
            }
            return accum;
          }, []);

          const purchaseInfoParams = {
            case_docket_sequence_numbers: [
              {
                court_code,
                case_number,
                sequence_numbers: seqNumbers,
              },
            ],
          };
          return getPartsPurchaseInfo(purchaseInfoParams)(dispatch, getState);
        }

        return Promise.resolve(response.body);
      },
      responseError => {
        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS,
          header: { status: 'done' },
          entries: { status: 'done' },
        });
        return dispatch(onFailure(responseError));
      }
    );
};

/*
function getDocumentsFromDockets(docketEntriesData, docket_no) {
  return _.chain(docketEntriesData)
    .filter(docket => docket.docket_no === docket_no)
    .map(docket => docket.binder.documents)
    .flatten()
    .value();
}
*/

const getDocumentsFromPacer = ({ court_code, case_number, docket_no, docket_seq }) => dispatch => {
  dispatch({
    type: types.RESET_SERVICE_ERROR,
  });

  dispatch({
    type: docketActionTypes.FETCH_DOCUMENTS.REQUEST,
    selectedDocket: {
      status: 'loading',
      docket_no,
      docket_seq,
    },
  });

  function onError(responseError) {
    dispatch({
      type: docketActionTypes.FETCH_DOCUMENTS.ERROR,
      selectedDocket: {
        status: 'done',
        docket_no,
        docket_seq,
        binder: {},
      },
    });

    return dispatch(onFailure(responseError));
  }

  function onSuccess(response) {
    if (response.body && !_.isEmpty(response.body.error)) {
      return onError({ response });
    }

    dispatch({
      type: docketActionTypes.FETCH_DOCUMENTS.SUCCESS,
      selectedDocket: {
        status: 'done',
        docket_no,
        docket_seq,
        binder: response.body,
      },
    });

    const purchaseInfoParams = {
      case_docket_sequence_numbers: [
        {
          court_code,
          case_number,
          sequence_numbers: [docket_seq],
        },
      ],
    };
    dispatch(getPartsPurchaseInfo(purchaseInfoParams));
  }

  const docketSeq = parseFloat(docket_seq) > 0 ? docket_seq : null;
  const docketNumber = parseInt(docket_no, 10) > 0 ? docket_no : docketSeq;
  if (!docketNumber) return onError();

  return request
    .post(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets/${docketNumber}/documents`)
    .then(onSuccess, onError);
};

const getDocumentsFromLocalDB = ({ court_code, case_number, docket_no, docket_seq }) => dispatch => {
  dispatch({
    type: types.RESET_SERVICE_ERROR,
  });

  dispatch({
    type: docketActionTypes.FETCH_DOCUMENTS.REQUEST,
    selectedDocket: {
      status: 'loading',
      docket_no,
      docket_seq,
    },
  });

  function onError(responseError) {
    dispatch({
      type: docketActionTypes.FETCH_DOCUMENTS.ERROR,
      selectedDocket: {
        status: 'done',
        docket_no,
        docket_seq,
        binder: {},
      },
    });

    return dispatch(onFailure(responseError));
  }

  function onSuccess(response) {
    if (response.body && !_.isEmpty(response.body.error)) {
      return onError({ response });
    }

    if (response.body.documents && response.body.documents.length === 0) {
      return dispatch(getDocumentsFromPacer({ court_code, case_number, docket_no, docket_seq }));
    }

    dispatch({
      type: docketActionTypes.FETCH_DOCUMENTS.SUCCESS,
      selectedDocket: {
        status: 'done',
        docket_no,
        docket_seq,
        binder: response.body,
      },
    });

    const purchaseInfoParams = {
      case_docket_sequence_numbers: [
        {
          court_code,
          case_number,
          sequence_numbers: [docket_seq],
        },
      ],
    };
    dispatch(getPartsPurchaseInfo(purchaseInfoParams));
  }

  const docketNumber = parseInt(docket_no, 10) ? docket_no : docket_seq;

  return request
    .get(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets/${docketNumber}/documents`)
    .then(onSuccess, onError);
};

export const getDocuments = ({ court_code, case_number, docket_no, docket_seq }) => (dispatch, getState) => {
  if (!docket_seq) {
    return;
  }

  // check for documents in existing entries
  const cachedDocket = getState()
    .getIn(['dockets', 'entries', 'data'])
    .find(docket => docket.get('docket_seq') === docket_seq);

  if (cachedDocket && cachedDocket.getIn(['binder', 'documents', 0])) {
    return dispatch({
      type: docketActionTypes.UPDATE_SELECTED_DOCKET,
      selectedDocket: {
        status: 'done',
        docket_no,
        ...cachedDocket.toJS(),
      },
    });
  }

  // retrieve from pacer
  return dispatch(getDocumentsFromLocalDB({ court_code, case_number, docket_no, docket_seq }));
};

// this is basically just a passthrough from the document response, located in links.order_pdf
// adding it here so we can update the state of the documents to inlcude the download links
export const orderPdf = (document, seqNum) => dispatch => {
  const url = document.links.order_pdf.href;
  const rpcLinkSegment = `/rpc/${url.split('/rpc/')[1]}`;

  dispatch({
    type: docketActionTypes.UPDATE_DOCUMENT,
    seqNum,
    document: { ...document, isLoading: true },
  });

  function onSuccess(response) {
    if (response.body && !_.isEmpty(response.body.error)) {
      return dispatch(onFailure({ response }));
    }

    const { document: updatedDocument } = response.body;

    dispatch({
      type: docketActionTypes.UPDATE_DOCUMENT,
      seqNum: updatedDocument.docket_no,
      document: { ...updatedDocument, isLoading: false },
    });

    return Promise.resolve(updatedDocument);
  }

  return request(rpcLinkSegment).then(onSuccess, responseError => dispatch(onFailure(responseError)));
};

export const purchaseDockets = params => (dispatch, getState) => {
  const { court_code, case_number } = params;

  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS,
    header: { status: 'loading' },
    entries: { status: 'loading' },
  });

  const currentState = getState().toJS();
  const clientCode = currentState.clientCodeReducer.sessionClientCode;
  const pacer_client = _.isEmpty(clientCode) ? window.Drupal.settings.clientCode : clientCode;

  function onSuccess(response) {
    if (!_.isEmpty(response.body.error)) {
      return dispatch(onFailure({ response }));
    }

    const receipt = _.get(response.body, 'receipts', {});
    receipt.screenshot = _.get(response.body, 'links.screenshot', {});

    dispatch({
      type: docketActionTypes.SET_RECEIPT,
      receipt,
    });

    return Promise.resolve(receipt);
  }

  const paramsStringNullRemoved = _.omitBy({ ...params, pacer_client }, _.isNil);

  return (
    request
      .post(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets/update`)
      .type('form')
      .send(paramsStringNullRemoved)
      // .set('Accept', 'application/json')
      .then(onSuccess, responseError => dispatch(onFailure(responseError)))
  );
};

export const resetDocketParts = () => dispatch => dispatch({ type: docketActionTypes.RESET_DOCKETS_PARTS });

export const updateDocketParts = docketParts => dispatch =>
  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts,
  });

export const updatePopup = popup => dispatch =>
  dispatch({
    type: docketActionTypes.DOCKETS_POPUP,
    popup,
  });

export const togglePopup = (params = {}) => dispatch => {
  dispatch(resetDocketParts());

  const popup = params.show ? { ...params } : { show: false };
  dispatch(updatePopup(popup));
};

export const loadFavoriteEmails = () => (dispatch, getState) => {
  const m = getState();

  dispatch({
    type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
    favoriteEmails: { emails: [], status: 'loading' },
  });

  return request
    .get('/rpc/session/user/docket/emails?sort=popular')
    .set('Accept', 'application/json')
    .then(
      response => {
        const { data, lastUpdated, receipt } = m.toJS().irSearch;
        data.push(['favoriteEmails', response.body]);

        // TODO: do we still need this?
        dispatch({
          type: types.RECEIVE_CASE_DATA,
          data_type: 'dockets',
          data,
          lastUpdated,
          receipt,
        });

        dispatch({
          type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
          favoriteEmails: { emails: response.body, status: 'done' },
        });
      },
      responseError => {
        dispatch(onFailure(responseError));
        dispatch({
          type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
          favoriteEmails: { emails: [], status: 'done' },
        });
      }
    );
};

export const loadLastFavoriteEmails = () => dispatch => {
  dispatch({
    type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
    favoriteEmails: { lastRecipients: [], status: 'loading' },
  });

  return request
    .get('/rpc/session/user/docket/emails/last_recipients')
    .set('Accept', 'application/json')
    .then(
      response => {
        dispatch({
          type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
          favoriteEmails: { lastRecipients: response.body, status: 'done' },
        });
      },
      responseError => {
        dispatch(onFailure(responseError));
        dispatch({
          type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
          favoriteEmails: { lastRecipients: [], status: 'done' },
        });
      }
    );
};

export const updateFavoriteEmails = ({ email_addresses }) => dispatch => {
  dispatch({
    type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
    favoriteEmails: { status: 'loading' },
  });

  return request
    .post('/rpc/session/user/docket/emails')
    .send({ email_addresses })
    .then(() => {
      dispatch({
        type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
        favoriteEmails: {
          lastRecipients: email_addresses,
          status: 'done',
        },
      });
    })
    .catch(responseError => {
      dispatch(onFailure(responseError));
      dispatch({
        type: docketActionTypes.DOCKETS_FAVORITE_EMAILS,
        favoriteEmails: {
          lastRecipients: email_addresses,
          status: 'done',
        },
      });
    });
};

// Taken from https://gist.github.com/joepie91/2664c85a744e6bd0629c#gistcomment-1787629
const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

function rpcProgress(url, dispatch, getState) {
  return request
    .get(url)
    .set('Accept', 'application/json')
    .then(
      response => {
        if (response.accepted) {
          // still processing, update percentage so progress bar updates.
          const percentage = Math.floor(response.body.task_progress);

          const state = getState().toJS();

          const docketParts = {
            ...state.dockets.docketParts,
            percentage,
          };

          dispatch({
            type: docketActionTypes.UPDATE_DOCKETS_PARTS,
            docketParts,
          });

          // wait 0.5s and then update progress again
          return wait(500).then(() => rpcProgress(url, dispatch, getState));
        }

        // when progress finishes, it returns a 302 response to the actual results, but it appears that  superagent
        // follows that redirect for us, so we should have a 200 response here
        if (response.status !== 200) {
          return dispatch(onFailure({ response }));
        }

        return Promise.resolve(response.body);
      },
      responseError => dispatch(onFailure(responseError))
    );
}

export const buyAllParts = params => (dispatch, getState) => {
  const currentState = getState().toJS();
  const prevDocketParts = currentState.dockets.docketParts;
  const clientCode = currentState.clientCodeReducer.sessionClientCode;
  const pacer_client = _.isEmpty(clientCode) ? window.Drupal.settings.clientCode : clientCode;

  params = { ...params, pacer_client };

  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...prevDocketParts,
      status: 'loading',
      step: 2,
      percentage: 0,
    },
  });

  const purchaseParams = {
    documents: params.documents,
    pacer_client,
  };

  return request
    .post('/rpc/dockets/documents/purchase')
    .type('json')
    .send(purchaseParams)
    .then(
      response => {
        if (!response.accepted || _.isEmpty(response.header.location)) {
          return dispatch(onFailure({ response }));
        }

        return rpcProgress(response.header.location, dispatch, getState);
      },
      responseError => dispatch(onFailure(responseError))
    )
    .then(
      purchaseResponse => {
        if (purchaseResponse.errors.length > 0) {
          const errors = _.uniq(purchaseResponse.errors.map(({ error }) => error));
          dispatch({
            type: docketActionTypes.UPDATE_DOCKETS_PARTS,
            docketParts: {
              purchased: purchaseResponse.purchased,
              step: 2,
              percentage: 0,
              status: 'done',
              errors,
              alreadyPurchased: true,
            },
          });

          return Promise.reject(purchaseResponse);
        }

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts: {
            purchased: purchaseResponse.purchased,
            step: 2,
            percentage: 0,
            errors: null,
            status: 'done',
          },
        });

        return Promise.resolve(purchaseResponse);
      },
      responseError => dispatch(onFailure(responseError))
    );
};

function downloadDocuments(params, dispatch, getState, type, combine_binders, printPreview = false) {
  const prevDocketParts = getState().toJS().dockets.docketParts;
  const prevPopup = getState().toJS().dockets.popup;

  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...prevDocketParts,
      status: 'loading',
      step: 3,
    },
  });

  const deliverParams = {
    delivery_method: 'download',
    documents: params.documents,
    type,
    combine_binders,
  };

  return request
    .post('/rpc/dockets/documents/deliver')
    .type('json')
    .send(deliverParams)
    .then(
      response => {
        if (!response.accepted || _.isEmpty(response.header.location)) {
          return dispatch(onFailure({ response }));
        }
        return rpcProgress(response.header.location, dispatch, getState);
      },
      responseError => dispatch(onFailure(responseError))
    )
    .then(
      deliverResponse => {
        // what we get here is
        // delivery_method: "download"
        // download_url: "http://..."
        // print_url: "http://..."
        // filename: "..."
        const link = printPreview ? deliverResponse.print_url : deliverResponse.download_url;
        const pretty_name = deliverResponse.filename;

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts: {
            ...prevDocketParts,
            email_address: params.email_address,
            step: 3,
            link,
            pretty_name,
          },
        });

        dispatch(updatePopup({ ...prevPopup, data: {} }));

        window.open(link);

        // TODO this is probably broken.  we dont have docket_numbers in params any longer.
        // I don't get what this is supposed to be doing
        if (params.docket_numbers && params.docket_numbers.length === 1) {
          return dispatch(
            getDocuments({
              court_code: params.court_code,
              case_number: params.case_number,
              docket_seq: params.docket_numbers[0],
              docket_no: parseInt(params.docket_numbers[0], 10),
            })
          );
        }

        return Promise.resolve(deliverResponse);
      },
      responseError => dispatch(onFailure(responseError))
    );
}

function emailParts(params, dispatch, getState, type, combine_binders) {
  const { dockets } = getState().toJS();

  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      ...dockets.docketParts,
      status: 'loading',
      step: 3,
    },
  });

  dispatch({
    type: docketActionTypes.DOCKETS_POPUP,
    popup: {
      ...dockets.popup,
      isEmailSending: true,
    },
  });

  const {documents, email_message, email_addresses} = params;

  const deliverParams = {
    combine_binders,
    type,
    documents,
    email_message,
    email_addresses,
    delivery_method: 'email',
  };

  return request
    .post('/rpc/dockets/documents/deliver')
    .type('json')
    .send(deliverParams)
    .then(
      response => {
        if (!response.accepted || _.isEmpty(response.header.location)) {
          return dispatch(onFailure({ response }));
        }
        return rpcProgress(response.header.location, dispatch, getState);
      },
      responseError => dispatch(onFailure(responseError))
    )
    .then(
      deliverResponse => {
        const { email_addresses, success, filename, download_url } = deliverResponse;

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts: {
            ...dockets.docketParts,
            email_address: email_addresses.join(', '),
            step: 3,
            success,
            alreadyPurchased: true,
          },
        });

        dispatch({
          type: docketActionTypes.DOCKETS_POPUP,
          popup: {
            ...dockets.popup,
            isEmailSending: false,
          },
        });

        const updatedEntriesData = dockets.entries.data.map(docket =>
          docket.docket_seq === dockets.selectedDocket.docket_seq
            ? { ...docket, [`${type}_filename`]: filename, [`${type}_download_url`]: download_url }
            : docket
        );

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS,
          entries: { ...dockets.entries, data: updatedEntriesData },
        });

        notify.success(`The filing has been mailed to ${params.email_addresses.join(', ')}`);

        dispatch(updateFavoriteEmails({ email_addresses }));

        if (params.docket_numbers && params.docket_numbers.length === 1) {
          return dispatch(
            getDocumentsFromLocalDB({
              court_code: params.court_code,
              case_number: params.case_number,
              docket_seq: params.docket_numbers[0],
              docket_no: parseInt(params.docket_numbers[0], 10),
            })
          );
        }

        return Promise.resolve('success');
      },
      responseError => dispatch(onFailure(responseError))
    );
}

export const downloadPdfZip = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'zip', false);

export const downloadPdfCombinedZipped = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'zip', true);

export const downloadPdfCombined = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'pdf', false);

export const previewPdf = params => (dispatch, getState) =>
  downloadDocuments(params, dispatch, getState, 'pdf', false, true);

export const emailPartsZip = params => (dispatch, getState) => emailParts(params, dispatch, getState, 'zip', false);

export const emailPartsPdfCombined = params => (dispatch, getState) =>
  emailParts(params, dispatch, getState, 'pdf', true);

export const getDocketParts = params => (dispatch, getState) => {
  dispatch({
    type: docketActionTypes.UPDATE_DOCKETS_PARTS,
    docketParts: {
      step: 1,
      status: 'loading',
      totalParts: 0,
    },
  });

  return request
    .post('/rpc/dockets/documents/cost')
    .type('json')
    .send(params)
    .then(
      response => {
        if (!response.accepted || _.isEmpty(response.header.location)) {
          return dispatch(onFailure({ response }));
        }

        return rpcProgress(response.header.location, dispatch, getState);
      },
      responseError => dispatch(onFailure(responseError))
    )
    .then(
      costResponse => {
        const totalCost = parseFloat(costResponse.total.cost);

        const docketParts = {
          partsToPurchase: costResponse.pacer.documents,
          totalParts: costResponse.total.documents,
          totalCost,
          step: 1,
          status: 'done',
        };

        // if no docs need to be purchased from pacer, move to step 2
        if (costResponse.pacer.documents === 0) {
          docketParts.step = 2;
          docketParts.alreadyPurchased = true;
        }

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts,
        });
      },
      responseError => dispatch(onFailure(responseError))
    );
};

export const getDocketHeader = params => {
  const { court_code, case_number } = params;
  return request().get(`/rpc/capi/cases/pacer/${court_code}/${case_number}/dockets/header`);
};

export const sendEmail = params => (dispatch, getState) => {
  const oldState = getState().toJS();

  const { email_address, email_message } = params;

  const email_addresses = email_address.split(/[, ]+/);

  const reqParams = {
    ...params,
    ...oldState.dockets.popup.data,
    to: email_addresses,
    message: email_message || '',
  };

  dispatch({
    type: docketActionTypes.DOCKETS_POPUP,
    popup: {
      ...oldState.dockets.popup,
      isEmailSending: true,
    },
  });

  return request
    .post('/rpc/dockets/documents/email')
    .type('json')
    .send(reqParams)
    .then(
      response => {
        if (typeof response === 'undefined' || response.status !== 200) {
          return dispatch(onFailure({ response }));
        }
        dispatch({
          type: docketActionTypes.DOCKETS_POPUP,
          popup: {
            ...oldState.dockets.popup,
            isEmailSending: false,
          },
        });

        dispatch({
          type: docketActionTypes.UPDATE_DOCKETS_PARTS,
          docketParts: {
            ...oldState.dockets.docketParts,
            email_address: params.email_address,
            // showFlash: true,
          },
        });

        notify.success(`The filing has been mailed to ${params.email_address}`);
      },
      responseError => dispatch(onFailure(responseError))
    )
    .then(() => dispatch(updateFavoriteEmails({ email_addresses })));
};

export const buyPdf = params => dispatch => {
  const { case_no, seqNum, number: documentNumber, court_code } = params;

  dispatch({
    type: docketActionTypes.UPDATE_DOCUMENT,
    seqNum,
    document: { number: documentNumber, isLoading: true },
  });

  function onError(responseError) {
    dispatch({
      type: docketActionTypes.UPDATE_DOCUMENT,
      seqNum,
      document: { number: documentNumber, isLoading: false },
    });

    return dispatch(onFailure(responseError));
  }

  function onSuccess(response) {
    const { body } = response;
    if (!_.isEmpty(body.error)) {
      return onError({ response });
    }

    const { document, links } = body;

    dispatch({
      type: docketActionTypes.UPDATE_DOCUMENT,
      seqNum,
      document: { ...document, links, isLoading: false },
    });

    return Promise.resolve(body);
  }

  return request
    .post(`/rpc/capi/cases/pacer/${court_code}/${case_no}/dockets/${seqNum}/documents/${documentNumber}`)
    .set('Accept', 'application/json')
    .then(onSuccess, onError);
};

export const filterDocketEntries = filter => (dispatch, getState) => {
  requestCaseData({}, dispatch);

  const state = getState();

  const { keyword = '' } = filter;
  const searchWords = keyword
    .toLowerCase()
    .split(' ')
    .filter(p => p !== '')
    .reverse();

  const data = state.toJS().irSearch.data.map(next => {
    const key = next[0];
    const value = next[1];

    if (key === 'entries') {
      const values = value.map(docketEntry => {
        const docketText = _.get(docketEntry, ['text', 'docket_text'], '');
        const isVisible =
          _.isEmpty(searchWords) || _.every(searchWords, word => docketText.toLowerCase().indexOf(word) > -1);
        return { ...docketEntry, isVisible };
      });
      return [key, values];
    }
    return next;
  });

  return dispatch({
    type: types.RECEIVE_CASE_DATA,
    data_type: 'dockets',
    data,
  });
};

export const updateRedact = updatedRedact => (dispatch, getState) => {
  const { redact } = getState()
    .get('dockets')
    .toJS();

  return dispatch({
    type: docketActionTypes.UPDATE_REDACT,
    redact: { ...redact, ...updatedRedact },
  });
};

export const deleteAndRepurchase = params => (dispatch, getState) => {
  const { redactSeqNum, doc_range, court_code, case_number } = params;

  const currentState = getState().toJS();
  const clientCode = currentState.clientCodeReducer.sessionClientCode;
  const client_code = _.isEmpty(clientCode) ? window.Drupal.settings.clientCode : clientCode;
  const repurchaseParams = {
    sequence_number: redactSeqNum,
    client_code,
  };

  return request
    .post(`/rpc/cases/${court_code}/${case_number}/docket/entry/delete_and_repurchase`)
    .send(repurchaseParams)
    .then(
      response => {
        if (!_.isEmpty(response.body.error)) {
          return dispatch(onFailure({ response }));
        }

        const receipt = _.get(response.body, 'items.receipts[0].text', {});
        receipt.source_data_id = _.get(response.body, 'source_data_id', {});

        dispatch({
          type: docketActionTypes.SET_RECEIPT,
          receipt,
        });

        const getDocketsParams = {
          ...params,
          receipt,
          docket_number_range: _.isEmpty(doc_range) ? null : doc_range,
        };

        return dispatch(getDockets(getDocketsParams));
      },
      responseError => dispatch(onFailure(responseError))
    );
};



// WEBPACK FOOTER //
// ./src/redux/actions/docketActions.js